import React from "react";

interface ExpandCollapseCardProps {
  title: string;
  description?: string;
  /** Optional bold lead-in shown above the description when expanded. */
  headline?: string;
  isOpen: boolean;
  onToggle: () => void;
  /** Optional render slot for fully custom expanded body content. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * Accordion card matching the Figma "Expand - Collapse Card" component.
 * Default/Hovered states show a right-pointing arrow + white title on a
 * #1f2428 -> #15181d gradient. Selected (expanded) flips the arrow to point
 * down and recolors the title to primary, then reveals the description body.
 */
export default function ExpandCollapseCard({
  title,
  description,
  headline,
  isOpen,
  onToggle,
  children,
  className,
}: ExpandCollapseCardProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className={`group relative w-full overflow-hidden text-left cursor-pointer rounded-[8px] bg-gradient-to-r from-surface-raised to-background transition-colors duration-300 ${
        className ?? ""
      }`}
    >
      {/* Hover lightens the surface (Figma "Hovered" state); not shown when expanded */}
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-0 bg-white/5 transition-opacity duration-300 ${
          isOpen ? "opacity-0" : "opacity-0 group-hover:opacity-100"
        }`}
      />
      {/* Phones get a tighter box: 32px of padding on every side plus a 32px
          arrow gap puts a collapsed card at 94px tall on a 390px screen, which
          is mostly empty space. 20px/16px brings it to 64px and keeps the same
          proportions. */}
      <div className="relative p-5 sm:p-[32px]">
        <div className="flex items-center gap-4 sm:gap-[32px]">
          <svg
            width="15"
            height="14"
            viewBox="0 0 15.043 14"
            fill="none"
            className={`flex-shrink-0 text-primary transition-transform duration-300 ease-out ${
              isOpen ? "rotate-[270deg]" : "rotate-180"
            }`}
          >
            <path
              d="M5.46846 0.390936L0.351547 6.05401C-0.117182 6.5759 -0.117182 7.423 0.351547 7.94314L5.46846 13.6096C5.94112 14.1315 6.70615 14.1288 7.17879 13.6096C7.64673 13.0868 7.64673 12.2432 7.17879 11.7204L4.1853 8.40809H13.788C14.4817 8.40809 15.043 7.78303 15.043 7.00938C15.043 6.2366 14.4817 5.61068 13.788 5.61068H4.16724L7.17879 2.28087C7.64673 1.75898 7.64673 0.914499 7.17879 0.392615C6.70691 -0.130143 5.94188 -0.131016 5.46846 0.390866V0.390936Z"
              fill="currentColor"
            />
          </svg>
          <span
            className={`text-[14px] leading-[24px] sm:text-[16px] sm:leading-[30px] transition-colors duration-300 ${
              isOpen ? "text-primary font-bold" : "text-white font-semibold"
            }`}
          >
            {title}
          </span>
        </div>

        {/* Revealed content — grid-rows trick animates to the exact height */}
        <div
          className={`grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            {/* Indent keeps the body aligned under the title: arrow (15px) plus
                the gap above, so it tracks the responsive gap. */}
            <div className="pt-3 sm:pt-4 pl-[31px] sm:pl-[47px]">
              {headline && (
                <p className="text-white text-[14px] leading-[24px] tracking-[0.25px] sm:text-[16px] sm:leading-[28px] sm:tracking-[0.5px] mb-2">
                  {headline}
                </p>
              )}
              {description && (
                <p className="text-text-default text-[14px] leading-[24px] tracking-[0.25px] sm:text-[16px] sm:leading-[28px] sm:tracking-[0.5px]">
                  {description}
                </p>
              )}
              {children}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
